import React, { useState, useEffect } from "react";
import { api } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { Upload, Calendar, Eye, EyeOff, MapPin, Plus } from "lucide-react";

const PRESETS_STORAGE_PREFIX = "scanmirein.location-presets.";

function getPresetStorageKey(companyId) {
  return `${PRESETS_STORAGE_PREFIX}${companyId || "default"}`;
}

function loadSavedPresets(companyId) {
  try {
    const raw = localStorage.getItem(getPresetStorageKey(companyId));
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((preset) => ({
        id: String(preset?.id || ""),
        name: String(preset?.name || "").trim(),
        locationIds: Array.isArray(preset?.locationIds)
          ? Array.from(new Set(preset.locationIds.filter(Boolean)))
          : [],
      }))
      .filter((preset) => preset.id && preset.name && preset.locationIds.length > 0)
      .slice(0, 20);
  } catch (_) {
    return [];
  }
}

function persistSavedPresets(companyId, presets) {
  localStorage.setItem(getPresetStorageKey(companyId), JSON.stringify(presets.slice(0, 20)));
}

const ActionLandingSettings = ({ actionId }) => {
  const { token, company } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({
    listedOnLandingPage: false,
    landingPageBannerUrl: "",
    landingPageVisibleFrom: "",
  });
  const [allLocations, setAllLocations] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [historyLocationPresets, setHistoryLocationPresets] = useState([]);
  const [savedLocationPresets, setSavedLocationPresets] = useState([]);
  const [presetName, setPresetName] = useState("");

  const locationPresets = React.useMemo(() => {
    const dedup = new Map();

    for (const preset of savedLocationPresets) {
      const ids = Array.isArray(preset?.locationIds)
        ? Array.from(new Set(preset.locationIds.filter(Boolean)))
        : [];
      const dedupKey = ids.sort().join("|");
      if (!dedupKey || dedup.has(dedupKey)) continue;
      dedup.set(dedupKey, {
        key: `saved:${preset.id}`,
        source: "Gespeichert",
        label: preset.name,
        names: ids
          .map((id) => allLocations.find((loc) => loc.id === id)?.name || id)
          .filter(Boolean),
        locationIds: ids,
      });
    }

    for (const preset of historyLocationPresets) {
      const ids = Array.isArray(preset?.locationIds)
        ? Array.from(new Set(preset.locationIds.filter(Boolean)))
        : [];
      const dedupKey = ids.sort().join("|");
      if (!dedupKey || dedup.has(dedupKey)) continue;
      dedup.set(dedupKey, {
        ...preset,
        key: preset.key || `history:${dedupKey}`,
      });
    }

    return Array.from(dedup.values()).slice(0, 16);
  }, [savedLocationPresets, historyLocationPresets, allLocations]);

  useEffect(() => {
    fetchSettings();
    fetchLocations();
  }, [actionId]);

  useEffect(() => {
    if (!actionId) return;
    if (!allLocations.length) {
      setHistoryLocationPresets([]);
      return;
    }
    fetchLocationPresets();
    setSavedLocationPresets(loadSavedPresets(company?.id));
  }, [actionId, allLocations, company?.id]);

  const fetchLocationPresets = async () => {
    try {
      const [actionsResp, stampCardsResp] = await Promise.all([
        api.listActions(token),
        api.stampCardsGet(),
      ]);

      const locationNameById = new Map((allLocations || []).map((loc) => [loc.id, loc.name]));
      const presets = [];

      const actions = Array.isArray(actionsResp)
        ? actionsResp.filter((a) => a?.id && a.id !== actionId).slice(0, 10)
        : [];

      const actionLocations = await Promise.all(
        actions.map(async (action) => {
          try {
            const resp = await api.getActionLocations(action.id);
            const ids = Array.isArray(resp?.locations)
              ? Array.from(new Set(resp.locations.map((l) => l.id).filter(Boolean)))
              : [];
            return {
              source: "Aktion",
              label: action?.title || "Aktion",
              locationIds: ids,
              names: Array.isArray(resp?.locations) ? resp.locations.map((l) => l.name) : [],
            };
          } catch (_) {
            return null;
          }
        })
      );
      presets.push(...actionLocations.filter(Boolean));

      const stampCards = Array.isArray(stampCardsResp?.stampCards)
        ? stampCardsResp.stampCards.slice(0, 10)
        : [];
      for (const card of stampCards) {
        const ids = Array.isArray(card?.locationIds)
          ? Array.from(new Set(card.locationIds.filter(Boolean)))
          : [];
        if (!ids.length) continue;
        presets.push({
          source: "Stempelkarte",
          label: card?.name || "Stempelkarte",
          locationIds: ids,
          names: ids.map((id) => locationNameById.get(id) || id),
        });
      }

      const dedup = new Map();
      for (const p of presets) {
        if (!Array.isArray(p?.locationIds) || p.locationIds.length === 0) continue;
        const key = [...p.locationIds].sort().join("|");
        if (!key || dedup.has(key)) continue;
        dedup.set(key, { ...p, key });
      }

      setHistoryLocationPresets(Array.from(dedup.values()).slice(0, 12));
    } catch (_) {
      setHistoryLocationPresets([]);
    }
  };

  const saveNamedPreset = () => {
    const name = String(presetName || "").trim();
    const ids = Array.isArray(selectedLocationIds)
      ? Array.from(new Set(selectedLocationIds.filter(Boolean)))
      : [];

    if (!name) {
      toast({
        title: "Bitte Preset-Namen eingeben",
        variant: "destructive",
      });
      return;
    }
    if (ids.length === 0) {
      toast({
        title: "Bitte mindestens einen Standort auswählen",
        variant: "destructive",
      });
      return;
    }

    const next = [
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name,
        locationIds: ids,
      },
      ...savedLocationPresets,
    ].slice(0, 20);

    setSavedLocationPresets(next);
    persistSavedPresets(company?.id, next);
    setPresetName("");
    toast({
      title: "Preset gespeichert",
      description: "Die Standortauswahl ist als Vorlage gespeichert.",
    });
  };

  const fetchLocations = async () => {
    try {
      const [allLocs, actionLocs] = await Promise.all([
        api.getLocations(),
        api.getActionLocations(actionId),
      ]);
      setAllLocations(allLocs.locations || []);
      setSelectedLocationIds((actionLocs.locations || []).map((l) => l.id));
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.getAction(token, actionId);
      if (response && response.action) {
        const action = response.action;
        setSettings({
          listedOnLandingPage: action.listedOnLandingPage || false,
          landingPageBannerUrl: action.landingPageBannerUrl || "",
          landingPageVisibleFrom: action.landingPageVisibleFrom
            ? new Date(action.landingPageVisibleFrom).toISOString().slice(0, 16)
            : "",
        });
      }
    } catch (error) {
      console.error("Failed to fetch action settings:", error);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const data = {
        listedOnLandingPage: settings.listedOnLandingPage,
        landingPageBannerUrl: settings.landingPageBannerUrl || null,
        landingPageVisibleFrom: settings.landingPageVisibleFrom || null,
      };

      await Promise.all([
        api.updateActionLandingSettings(token, actionId, data),
        api.setActionLocations(actionId, selectedLocationIds),
      ]);

      toast({
        title: "Einstellungen gespeichert",
        description: "Die Landingpage-Einstellungen wurden erfolgreich aktualisiert.",
      });
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Die Einstellungen konnten nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Ungültiger Dateityp",
        description: "Bitte wähle ein Bild aus (JPG, PNG, etc.)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Datei zu groß",
        description: "Das Bild darf maximal 5MB groß sein.",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);

      const data = await api.uploadActionBanner(token, actionId, file);
      
      setSettings((prev) => ({
        ...prev,
        landingPageBannerUrl: data.url,
      }));
      toast({
        title: "Banner hochgeladen",
        description: "Das Banner wurde erfolgreich hochgeladen.",
      });
    } catch (error) {
      toast({
        title: "Upload fehlgeschlagen",
        description: "Das Banner konnte nicht hochgeladen werden.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-background rounded-lg border p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Landingpage-Einstellungen</h3>
        <p className="text-sm text-muted-foreground">
          Konfiguriere, wie deine Aktion auf der öffentlichen Landingpage angezeigt wird.
        </p>
      </div>

      {/* List on Landing Page Toggle */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="listed" className="text-base font-medium">
            Auf Landingpage listen
          </Label>
          <p className="text-sm text-muted-foreground">
            Zeige diese Aktion auf der öffentlichen Landingpage an
          </p>
        </div>
        <Switch
          id="listed"
          checked={settings.listedOnLandingPage}
          onCheckedChange={(checked) =>
            setSettings((prev) => ({ ...prev, listedOnLandingPage: checked }))
          }
        />
      </div>

      {settings.listedOnLandingPage && (
        <>
          {/* Banner Upload */}
          <div className="space-y-2">
            <Label htmlFor="banner">Banner-Bild</Label>
            <div className="flex gap-3">
              <Input
                id="banner"
                type="text"
                placeholder="Banner-URL eingeben oder Datei hochladen..."
                value={settings.landingPageBannerUrl}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    landingPageBannerUrl: e.target.value,
                  }))
                }
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                disabled={uploading}
                onClick={() => document.getElementById("banner-file")?.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Lädt..." : "Upload"}
              </Button>
              <input
                id="banner-file"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleBannerUpload}
              />
            </div>
            {settings.landingPageBannerUrl && (
              <div className="mt-2">
                <img
                  src={settings.landingPageBannerUrl}
                  alt="Banner Preview"
                  className="w-full h-40 object-cover rounded-lg border"
                />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Empfohlene Größe: 1200x600px. Max. 5MB (JPG, PNG)
            </p>
          </div>

          {/* Visible From Date */}
          <div className="space-y-2">
            <Label htmlFor="visibleFrom" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Sichtbar ab (optional)
            </Label>
            <Input
              id="visibleFrom"
              type="datetime-local"
              value={settings.landingPageVisibleFrom}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  landingPageVisibleFrom: e.target.value,
                }))
              }
            />
            <p className="text-xs text-muted-foreground">
              Wenn gesetzt, wird die Aktion erst ab diesem Zeitpunkt auf der Landingpage
              sichtbar. Leer lassen für sofortige Sichtbarkeit.
            </p>
          </div>

          {/* Locations */}
          <div className="space-y-3">
            <Label className="text-base font-medium flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Standorte (optional)
            </Label>
            <p className="text-sm text-muted-foreground">
              Wähle die Standorte aus, an denen diese Aktion gültig ist. Ohne Auswahl gilt sie für alle Standorte.
            </p>
            {locationPresets.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">Verlauf / Schnellvorlagen</p>
                <div className="flex flex-wrap gap-2">
                  {locationPresets.map((preset) => (
                    <button
                      key={preset.key}
                      type="button"
                      className="text-xs border rounded px-2 py-1 hover:bg-muted"
                      title={preset.names?.join(", ") || ""}
                      onClick={() => setSelectedLocationIds(Array.isArray(preset.locationIds) ? preset.locationIds : [])}
                    >
                      {preset.source}: {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Input
                placeholder="Preset-Name (z. B. Innenstadt)"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
              />
              <Button type="button" variant="outline" onClick={saveNamedPreset}>
                Auswahl speichern
              </Button>
            </div>

            {allLocations.length === 0 ? (
              <div className="border border-dashed rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  Noch keine Standorte erfasst
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open("/locations", "_blank")}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Standort hinzufügen
                </Button>
              </div>
            ) : (
              <div className="space-y-2 border rounded-lg p-3">
                {allLocations.map((location) => (
                  <label
                    key={location.id}
                    className="flex items-start gap-3 p-2 hover:bg-muted rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedLocationIds.includes(location.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLocationIds((prev) => [...prev, location.id]);
                        } else {
                          setSelectedLocationIds((prev) =>
                            prev.filter((id) => id !== location.id)
                          );
                        }
                      }}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{location.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {location.addressLine1}, {location.postalCode} {location.city}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Preview Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <Eye className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Vorschau-Info</p>
                <p className="text-xs text-blue-700 mt-1">
                  Die Aktion wird mit Titel, Banner, Aktionszeitraum und deinem
                  Organisationsnamen/-logo angezeigt.
                  {settings.landingPageVisibleFrom && (
                    <span className="block mt-1">
                      Sichtbar ab:{" "}
                      {new Date(settings.landingPageVisibleFrom).toLocaleString("de-DE")}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleSave} disabled={loading}>
          {loading ? "Speichert..." : "Einstellungen speichern"}
        </Button>
      </div>
    </div>
  );
};

export default ActionLandingSettings;
